/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEvent,
  MouseEventHandler,
  ReactNode
} from 'react';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import isundefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  CRUDPermissions,
  DeviceFiles,
  GenericObject,
  Link,
  PlatformDeviceType
} from '@ion/core/src/types/entities';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {dlsAlt, dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import CloseButton from '../components/buttons/CloseButton';
import Tooltip from '../components/tooltip/Tooltip';
import {useCloseKeyCommand} from '../hooks/useCloseKeyCommand';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useHasInheritable} from '../hooks/useHasInheritable';
import {useMedia} from '../hooks/useMedia';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import Render from '../render/Render';
import useThemeContext from '../theme/useThemeContext';
import {DLSTheme, IconSize} from '../types/types';
import {CancelButton} from './controls/CancelButton';
import {SaveButton} from './controls/SaveButton';
import DownloadFileView from './DownloadFileView';
import {getClasses, getPhoneFooterJustify} from './editorStyles';

export interface EditorDialogType {
  children: ReactNode;
  dialogTitle?: string | Node;
  onAuditClick?: (e: MouseEvent) => void;
  onEditClick?: MouseEventHandler;
  open: boolean;
  onCloseDialog?: (e: MouseEvent) => void;
  onCancelDialog?: (e: MouseEvent<HTMLButtonElement>) => void,
  onPrimaryButtonClick?: (event: MouseEvent<HTMLButtonElement>) => void,
  primaryButtonLoading?: boolean,
  primaryButtonDisabled?: boolean,
  styleOverrides?: (theme: DLSTheme) => GenericObject,
  omitActions?: boolean,
  primaryButtonDisabledText?: string,
  disableViewEdit?: boolean;
  isViewDialog?: boolean;
  onDownloadFile?: Function;
  onReleaseNote?: Function;
  deviceFile?: DeviceFiles;
  isDeviceFileView?: boolean;
  deviceTypes?: PlatformDeviceType;
  isLoggedinUserAccount?: boolean;
  hasUpdatePermission?: boolean;
  showHideEditBtn?: boolean;
  userHasPermissionToEdit?: boolean;
  hasUpdate?: boolean;
  groupsPermissions?: CRUDPermissions;
  hasInheritableGroups?: boolean;
  inheritable?: boolean;
  globalGroup?: Account | Link;
  account?: Link;
  ancestorsGroups?: GenericObject;
  renderEditConfigButton?: () => JSX.Element;
}

const styles = (theme: DLSTheme, isCurrent = false) => ({
  formPaper : {
    backgroundColor                : `${dlsColor(isCurrent ? 'white' : 'grey6')} !important`,
    maxWidth                       : '1068px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    borderRadius                   : isCurrent ? 12 : 0,
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },

  paperScrollPaper : {
    maxHeight : 'calc(100% - 94px)'
  },

  scrollPaper : {
    flexDirection  : 'column',
    justifyContent : 'flex-end'
  },

  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor(isCurrent ? 'pastelTeal' : 'grey4')}`
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },

  currentCloseButton : {
    position : 'absolute',
    right    : '19px'
  },

  dialogTitle : {
    marginLeft   : '20px',
    whiteSspace  : 'nowrap',
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  },
  formDialogContent : {
    ...(isCurrent ? {display: 'flex', flexDirection: 'column', gap: 12} : {}),
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  formActions : {
    ...(isCurrent ? {
      boxShadow : dlsShadow(5.3),
      zIndex    : 1
    } : {
      borderTop : `solid 1px ${dlsColor('grey4')}`
    }),
    [theme.breakpoints.down('sm')] : {
      justifyContent : getPhoneFooterJustify(isCurrent),
      padding        : '8px 16px 8px 40px'
    }
  },
  primaryButton : {
    width       : isCurrent ? '68px' : '191px',
    marginRight : '28px',
    ...(isCurrent ? {
      '& div' : {
        width : '68px'
      },
      '& .MuiButton-root' : {
        fontWeight : '500 !important',
        height     : '29px',
        fontFamily : 'Inter',
        fontSize   : '14px'
      }
    } : {}),
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  },
  downloadFile : {
    backgroundColor : dlsColor('grey1'),
    height          : '48px',
    position        : 'relative'
  },
  downloadFileButton : {
    color      : dlsColor('white'),
    lineHeight : '24px'
  }
});

const iconsTooltip = makeStyles((theme) => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'tooltip')
  }
}));

function CustomTooltip(props) {
  return <Tooltip arrow classes={iconsTooltip()} {...props} placement='bottom' />;
}

const maxCharacters = 80;

/**
 * @EditorDialog
 * Wrapper component for form Dialogs
 *
 * @param {Node} children: Component that is to be wrapped
 * @param {Node | string} dialogTitle: Dialog title
 * @param {boolean} open: should the dialog be open
 * @param {Function} onCloseDialog: callback for when the dialog requests to be closed
 * @param {Function} onCancelDialog: callback for when the user clicks the cancel button
 * @param {Function} onPrimaryButtonClick: callback to execute on main CTA button click
 * @param {boolean} primaryButtonLoading: if true we show the button spinner
 * @param {Function} styleOverrides: function that returns styles to override the base
 *   component styles
 *
 * ```typescript
 *
 *
import {EditorDialog} from '@ion/dls/src/editors/EditorDialog';
 *
 * <EditorDialog open={open}
 *   dialogTitle='Add Asset'
 *   onCloseDialog={onCloseDialog}
 *   onCancelDialog={onCancelDialog}
 *   onPrimaryButtonClick={onPrimaryButtonClick}
 *   primaryButtonLoading={true}>
 *   <Component />
 * </EditorDialog>
 *
 * ```
 *
 */
export const EditorDialog = ({
  children,
  dialogTitle,
  onAuditClick,
  onEditClick,
  open,
  deviceFile,
  onCloseDialog,
  onCancelDialog,
  onPrimaryButtonClick,
  styleOverrides,
  onDownloadFile,
  onReleaseNote,
  groupsPermissions,
  globalGroup,
  account,
  deviceTypes,
  primaryButtonDisabledText,
  ancestorsGroups,
  renderEditConfigButton,
  hasUpdate = true,
  hasUpdatePermission = false,
  primaryButtonLoading = false,
  hasInheritableGroups = false,
  inheritable = false,
  primaryButtonDisabled = false,
  omitActions = false,
  showHideEditBtn = true,
  userHasPermissionToEdit = true,
  disableViewEdit = false,
  isViewDialog = false,
  isDeviceFileView = false,
  isLoggedinUserAccount = false
}: EditorDialogType) => {
  const {
    isCurrent : isCurrentTheme,
    isLegacy  : isLegacyTheme
  } = useThemeContext();
  
  const dialogStyles         = useExtendStyles((theme) => styles(theme, isCurrentTheme), styleOverrides);
  const classes              = dialogStyles();
  const {isPhone}            = useMedia();
  const hasPrimaryButtonText = isundefined(primaryButtonDisabledText) && primaryButtonDisabled === false;
  const globalAccountId      = entityId(globalGroup);
  const groupAccountId       = entityId(account);
  const onKeyCommand         = useCloseKeyCommand((omitActions || isViewDialog) ? onCloseDialog : undefined);

  const hasInheritable = useHasInheritable({
    ancestorsGroups,
    groupAccountId,
    globalAccountId,
    inheritable
  });

  const checkEnableDisableBtn = () => {
    const disableBtn = disableViewEdit && !isLoggedinUserAccount;
    if (hasInheritableGroups) {
      return !groupsPermissions?.hasUpdatePermission || hasInheritable;
    } if (isDeviceFileView) {
      return hasUpdate;
    } if (!hasUpdatePermission) {
      return true;
    } else if (disableBtn && !hasUpdatePermission) {
      return true;
    }
    return disableBtn;
  };

  return (
    <Dialog open={open}
      onKeyDown={onKeyCommand}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <Render.If condition={isLegacyTheme}>
            <div onClick={onCloseDialog} className={classes.closeButton}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </div>
          </Render.If>
          {dialogTitle && (
            <>
              {dialogTitle?.length > maxCharacters && !isPhone
                ? (
                  <CustomTooltip title={<span>{dialogTitle}</span>}>
                    <Typography className={classes.dialogTitle} component='h1' variant='h2'>
                      {dialogTitle?.substring(0, maxCharacters).concat('...')}
                    </Typography>
                  </CustomTooltip>
                )
                : (
                  <Typography className={classes.dialogTitle} component='h1' variant='h2'>
                    {dialogTitle}
                  </Typography>
                )}
            </>
          )}
          <Render.If condition={isCurrentTheme}>
            <div className={classes.currentCloseButton}>
              <CloseButton onClick={onCloseDialog} />
            </div>
          </Render.If>
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        {children}
      </DialogContent>
      {(!omitActions && !isViewDialog && (onCancelDialog || onPrimaryButtonClick)) && (
        <DialogActions className={classes.formActions}>
          {onCancelDialog && <CancelButton onCancel={onCancelDialog} />}
          {onPrimaryButtonClick && (
            <div className={classes.primaryButton}>
              <Tooltip arrow
                title={primaryButtonDisabledText}
                disableHoverListener={
                  (!primaryButtonDisabled && !!primaryButtonDisabledText) ||
                  hasPrimaryButtonText ||
                  isLoggedinUserAccount
                }>
                <div>
                  <SaveButton onSave={onPrimaryButtonClick}
                    loading={primaryButtonLoading}
                    disabled={(primaryButtonLoading || primaryButtonDisabled) && !isLoggedinUserAccount} />
                </div>
              </Tooltip>
            </div>
          )}
        </DialogActions>
      )}
      {(isViewDialog && (onAuditClick || onEditClick)) && (
        <DialogActions className={classes.formActions}>
          {onCancelDialog && (
            <CancelButton onCancel={onCancelDialog} />
          )}
          {onAuditClick && (
            <PrimaryButton onClick={onAuditClick}>
              {translate('common:audit')}
            </PrimaryButton>
          )}
          {showHideEditBtn && userHasPermissionToEdit && onEditClick && (
            <PrimaryButton disabled={checkEnableDisableBtn()}
              onClick={onEditClick}>
              {translate('common:edit')}
            </PrimaryButton>
          )}
          {renderEditConfigButton?.()}
          {isDeviceFileView && (
            <DownloadFileView deviceFile={deviceFile}
              hasUpdatePermission={hasUpdatePermission}
              deviceTypes={deviceTypes}
              downloadFile={onDownloadFile}
              releaseNote={onReleaseNote}
              downloadFileclass={classes.downloadFile}
              downloadFileButtonClass={classes.downloadFileButton} />
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EditorDialog;
